// TODO: Fix type safety - UnifiedCampsiteSearchService.search() returns 'any' type
// Need to add proper TypeScript types for search results to eliminate unsafe-* warnings
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */

import { UnifiedCampsiteSearchService } from '~/apps/search/infrastructure/UnifiedCampsiteSearchService'
import type { SearchFilters } from '~/types/search'

export interface BreadcrumbCounts {
  bookableFacetHierarchy?: Record<string, number>
  facetHierarchy?: Record<string, number>
}

export function useBreadcrumbCounts(searchFilters: ComputedRef<SearchFilters>) {
  const route = useRoute()
  const langCode = (route.params.lang as string) || 'en-gb'
  const unifiedSearchService = new UnifiedCampsiteSearchService()

  onUnmounted(() => {
    if (unifiedSearchService && unifiedSearchService.cancel) {
      unifiedSearchService.cancel()
    }
  })

  const hasFacets = computed(
    () => (searchFilters.value.facets ?? []).length > 0,
  )

  const filters = computed(() => {
    return {
      ...searchFilters.value,
      limit: 0,
      base: null,
      search_url: null,
      page: null,
      sort: null,
      facet_counts_only: 'hierarchy',
    }
  })

  const cacheKey = computed(() => {
    return `breadcrumbCounts-${JSON.stringify(filters.value)}`
  })

  const { data } = useAsyncData<BreadcrumbCounts>(
    cacheKey.value,
    async () => {
      try {
        const results = await unifiedSearchService.search(
          langCode,
          filters.value,
        )
        // Symmetric fallback: ensure both maps are always populated.
        // For dated searches, only bookable results exist so the API may
        // only return bookableFacetHierarchy — the two are equivalent in
        // that context. This lets downstream linkability logic use either
        // map without null checks, at the cost of not distinguishing
        // "total (free+bookable)" from "bookable-only" when one is missing.
        if (typeof results.bookableFacetHierarchy === 'undefined') {
          results.bookableFacetHierarchy = results.facetHierarchy
        }
        if (typeof results.facetHierarchy === 'undefined') {
          results.facetHierarchy = results.bookableFacetHierarchy
        }
        return results
      } catch (error) {
        // might be a 404, so we don't want to log an error
        // Only log serializable error details to avoid Nuxt stringify errors
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        const errorDetails =
          error && typeof error === 'object' && 'issues' in error
            ? error.issues
            : undefined
        console.error(
          'Error fetching hierarchy counts:',
          errorDetails || errorMessage,
        )
        return { bookableFacetHierarchy: {}, facetHierarchy: {} }
      }
    },
    {
      watch: [searchFilters],
      server: true,
      lazy: process.client,
      dedupe: 'cancel',
      getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
    },
  )

  return {
    breadcrumbCounts: data,
    hasFacets,
  }
}
